// Resolve a content type that the backend's audio MIME allowlist will accept.
//
// Why this exists: the hike-highlight audio slot lets users pick a file, but the
// browser reports an .mp4 the user intends as audio with `video/mp4` (or with an
// empty type). The blob is presigned with whatever we send here, and the server
// then rejects `video/mp4` for an `audio` MediaAttachment row — the upload is
// silently dropped and the saved highlight has no audio. Mapping the file to an
// allowed audio MIME (by extension when the declared type isn't already an audio
// type) lets the upload land and an <audio> element plays the audio track.
//
// Keep this list in sync with MediaAttachment::ALLOWED_CONTENT_TYPES['audio'].
const ALLOWED_AUDIO_CONTENT_TYPES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/aac',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'audio/webm'
];

// Default for browser-recorded clips that arrive without a usable type/extension.
const DEFAULT_AUDIO_CONTENT_TYPE = 'audio/webm';

const EXTENSION_TO_AUDIO_CONTENT_TYPE: Record<string, string> = {
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  webm: 'audio/webm'
};

// MediaRecorder emits types with codec params (e.g. "audio/webm;codecs=opus");
// the allowlist is keyed on the base MIME, so strip params before comparing.
const normalizeBaseType = (type: string): string =>
  type.split(';')[0].trim().toLowerCase();

const extensionOf = (filename: string): string =>
  filename.includes('.') ? filename.split('.').pop()!.toLowerCase() : '';

/**
 * Given a file's name and the browser-declared MIME type, return a content type
 * the audio allowlist accepts.
 *
 * - An already-allowed audio type is returned unchanged (covers recordings and
 *   correctly-typed picks like .mp3 / .m4a reported as audio/*).
 * - Otherwise the extension decides (this is what rescues an .mp4 the browser
 *   labelled video/mp4 or left untyped).
 * - Falling through to audio/webm matches the prior default for typeless blobs.
 */
export const resolveAudioContentType = (
  filename: string | undefined,
  declaredType: string | undefined
): string => {
  const base = normalizeBaseType(declaredType || '');
  if (ALLOWED_AUDIO_CONTENT_TYPES.includes(base)) return base;

  const byExtension = EXTENSION_TO_AUDIO_CONTENT_TYPE[extensionOf(filename || '')];
  if (byExtension) return byExtension;

  // No allowed declared type and no recognised extension: keep the existing
  // fallback rather than forwarding a non-audio type the server would reject.
  return DEFAULT_AUDIO_CONTENT_TYPE;
};

export default resolveAudioContentType;
